import { useEffect, useState, useCallback } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Check } from "lucide-react";
import { toast } from "sonner";

const NO_AUTOFILL = { autoComplete: "off", autoCorrect: "off", autoCapitalize: "off", spellCheck: false };

function EditableRow({ it, testPrefix, onDelete, onRename }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(it);

  const save = () => {
    const v = val.trim().split(/\s+/).join(" ");
    setEditing(false);
    if (v && v.toLowerCase() !== it.toLowerCase()) onRename(it, v);
    else setVal(it);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 px-3 py-1 border-b last:border-0 border-slate-100">
        <input data-testid={`${testPrefix}-edit-input-${it}`} value={val} autoFocus {...NO_AUTOFILL}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save();
            if (e.key === "Escape") { setVal(it); setEditing(false); }
          }}
          className="flex-1 min-w-0 text-sm border border-slate-300 rounded-sm px-2 py-0.5" />
        <button data-testid={`${testPrefix}-edit-save-${it}`} onClick={save} title="Salva modifica"
          className="text-emerald-600 hover:text-emerald-800 transition-colors">
          <Check className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div data-testid={`${testPrefix}-item-${it}`}
      className="flex items-center justify-between px-3 py-1.5 border-b last:border-0 border-slate-100 text-sm">
      <span className="notranslate" translate="no">{it}</span>
      <div className="flex items-center gap-2 shrink-0">
        <button data-testid={`${testPrefix}-edit-${it}`} title="Modifica voce"
          onClick={() => { setVal(it); setEditing(true); }}
          className="text-slate-400 hover:text-blue-700 transition-colors">
          <Pencil className="w-3.5 h-3.5" />
        </button>
        <button data-testid={`${testPrefix}-delete-${it}`} onClick={() => onDelete(it)}
          className="text-slate-400 hover:text-red-700 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function Section({ title, items, onAdd, onDelete, onRename, testPrefix }) {
  const [value, setValue] = useState("");
  return (
    <div className="border border-slate-300 rounded-sm">
      <div className="bg-slate-100 px-3 py-2 border-b border-slate-300 text-sm font-bold uppercase tracking-tight">{title} ({items.length})</div>
      <div className="max-h-[32rem] overflow-y-auto">
        {items.map((it) => (
          <EditableRow key={it} it={it} testPrefix={testPrefix} onDelete={onDelete} onRename={onRename} />
        ))}
      </div>
      <div className="flex gap-2 p-2 border-t border-slate-200">
        <Input data-testid={`${testPrefix}-add-input`} value={value} onChange={(e) => setValue(e.target.value)}
          placeholder="Nuovo..." className="rounded-sm h-8 text-sm" {...NO_AUTOFILL} />
        <Button data-testid={`${testPrefix}-add-button`} size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-700 text-white h-8"
          onClick={() => { if (value.trim()) { onAdd(value); setValue(""); } }}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function MasterDataDialog({ open, onClose }) {
  const [data, setData] = useState({ personnel: [], sites: [], subcontractors: [] });

  const load = useCallback(async () => {
    const res = await api.get("/master");
    setData(res.data);
    // api è un import di modulo stabile
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) load(); }, [open, load]);

  const act = async (fn) => {
    try { await fn(); await load(); } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const rename = (kind, oldName, newName) =>
    act(() => api.put(`/master/${kind}/${encodeURIComponent(oldName)}`, { new_name: newName }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-sm max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="master-data-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-lg font-bold tracking-tight">Anagrafiche base</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Section title="Personale" items={data.personnel} testPrefix="personnel"
            onAdd={(n) => act(() => api.post("/master/personnel", { name: n }))}
            onDelete={(n) => act(() => api.delete(`/master/personnel/${encodeURIComponent(n)}`))}
            onRename={(o, n) => rename("personnel", o, n)} />
          <Section title="Cantieri" items={data.sites} testPrefix="sites"
            onAdd={(n) => act(() => api.post("/master/sites", { name: n }))}
            onDelete={(n) => act(() => api.delete(`/master/sites/${encodeURIComponent(n)}`))}
            onRename={(o, n) => rename("sites", o, n)} />
          <Section title="Subappaltatori" items={data.subcontractors} testPrefix="subs"
            onAdd={(n) => act(() => api.post("/master/subcontractors", { name: n }))}
            onDelete={(n) => act(() => api.delete(`/master/subcontractors/${encodeURIComponent(n)}`))}
            onRename={(o, n) => rename("subcontractors", o, n)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
